<template>
  <div>
    <p class="has-text-weight-bold is-size-4">{{ $t("mp:profile.title") }}</p>

    <div class="mp-card">
      <div class="is-flex mb-3">
        <p class="has-text-weight-bold is-size-5">
          {{ $t("mp:profile.personal_data.title") }}
        </p>

        <div class="ml-auto">
          <b-button icon-left="power-off" type="is-danger" @click="logout">{{
            $t("mp:profile.button.logout")
          }}</b-button>
        </div>
      </div>

      <template v-if="loaded">
        <form @submit.prevent="save">
          <validation-observer ref="observer">
            <div class="columns">
              <div class="column">
                <BInputWithValidation
                  :label="$t('mp:profile.personal_data.last_name.label')"
                  rules="required"
                  label-position="on-border"
                  v-model="merchant.lastName"
                />
              </div>

              <div class="column">
                <BInputWithValidation
                  :label="$t('mp:profile.personal_data.first_name.label')"
                  rules="required"
                  label-position="on-border"
                  v-model="merchant.firstName"
                />
              </div>
            </div>

            <p class="has-text-weight-bold is-size-5 mb-4">
              {{ $t("mp:profile.country.title") }}
            </p>

            <div class="columns mt-5">
              <div class="column">
                <CountrySelect
                  v-model="merchant.countryId"
                  :label="$t('mp:profile.personal_data.language.label')"
                />
              </div>
            </div>

            <p class="has-text-weight-bold is-size-5 mb-4">
              {{ $t("mp:profile.contact.title") }}
            </p>

            <div class="columns mt-5">
              <div class="column">
                <BInputWithValidation
                  :label="$t('mp:profile.contact.email.label')"
                  rules="required|email"
                  label-position="on-border"
                  v-model="merchant.email"
                />
              </div>

              <div class="column">
                <PhoneInput
                  :label="$t('mp:profile.contact.phone.label')"
                  v-model="merchant.phone"
                  :mask="
                    merchant.countryId
                      ? getUserCountry(merchant.countryId).phoneFormat
                      : ''
                  "
                  :prefix="
                    merchant.countryId
                      ? getUserCountry(merchant.countryId).phoneCountryCode
                      : ''
                  "
                />
              </div>
            </div>
            <div class="columns mt-5">
              <div class="column">
                <p class="has-text-weight-bold is-size-5 mb-5">
                  {{ $t("mp:user.auto_notifications.title") }}
                </p>
                <b-field>
                  <b-switch
                    v-model="merchant.autoNotificationAtSuccessPrescore"
                  >
                    {{ $t("mp:profile.auto.notification.at.success.prescore") }}
                  </b-switch>
                </b-field>
                <b-field>
                  <b-switch v-model="merchant.autoNotificationAtPrescore">
                    {{ $t("mp:profile.auto.notification.at.prescore") }}
                  </b-switch>
                </b-field>
              </div>
            </div>
            <b-button
              v-if="canUpdateSelf"
              native-type="submit"
              :loading="submitted"
              class="is-success"
              >{{ $t("mp:profile.button.save") }}
            </b-button>
          </validation-observer>
        </form>
      </template>
      <LoadingSkeleton v-else />
    </div>
  </div>
</template>

<script>
import LoadingSkeleton from "../components/LoadingSkeleton";
import BInputWithValidation from "../components/input/BInputWithValidation";
import PhoneInput from "../components/input/PhoneInput";
import i18n from "../plugins/i18n";
import { AUTHORITIES } from "../utils/authorities";
import CountrySelect from "../components/input/CountrySelect.vue";
import { mapGetters } from "vuex/dist/vuex.common.js";

export default {
  name: "Profile",
  components: {
    PhoneInput,
    BInputWithValidation,
    LoadingSkeleton,
    CountrySelect,
  },
  title: "mp:page.profile.title",
  data() {
    return {
      merchant: {
        lastName: "",
        firstName: "",
        phone: "",
        email: "",
        countryId: null,
        autoNotificationAtSuccessPrescore: false,
        autoNotificationAtPrescore: false,
      },
      loaded: false,
      submitted: false,
    };
  },
  computed: {
    ...mapGetters({
      countries: "i18n/countries",
    }),
    canUpdateSelf() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.USER.UPDATE_SELF,
        AUTHORITIES.USER.ADMIN,
      ]);
    },
  },
  async mounted() {
    if (!this.$store.getters["session/canAccessPortal"]) {
      await this.$store.dispatch("session/refreshJwtFromServer");
      if (!this.$store.getters["session/canAccessPortal"]) {
        await this.$store.dispatch("application/pollStatus");

        if (this.$store.getters["application/applicationStatus"]) {
          await this.$router.push({
            name: "Application",
          });
        } else {
          console.warn("User can not access portal, logging out");
          this.logout();
        }
        return;
      }
    }

    await this.$store.dispatch("profile/fetch");
    this.merchant = this.$store.getters["profile/get"];
    this.loaded = true;
  },
  methods: {
    async save() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      this.submitted = true;

      try {
        await this.$store.dispatch("profile/save", this.merchant);

        this.$buefy.toast.open({
          message: i18n.t("mp:profile.save.success.message"),
          type: "is-success",
        });
      } catch (ex) {
        this.$buefy.toast.open({
          message: i18n.t("mp:profile.save.failure.message"),
          type: "is-danger",
        });
      }

      this.submitted = false;
    },
    logout() {
      this.$store.dispatch("session/logout");
      this.$router.push({
        name: "Login",
      });
    },
    getUserCountry(countryId) {
      const country = this.countries.find(
        (country) => country.id === countryId
      );
      country.phoneFormat = `${country.phoneFormat}`.replaceAll(/\d/g, "#");
      return country;
    },
  },
};
</script>
